import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import EventCard from '../components/EventCard';
import Pill from '../components/Pill';
import { useAuth } from '../context/AuthContext';
import { publicEventsService, PublicEvent } from '../services/publicEventsService';
import { formatEventDate } from '../utils/dates';

interface HomeViewProps {
    openRegister: () => void;
}

const RAW_CATEGORIES = [
    { id: 'ALL', label: 'Todos', emoji: '🎭' },
    { id: 'FESTIVAL', label: 'Festivales', emoji: '🎉' },
    { id: 'CONCERT', label: 'Conciertos', emoji: '🎵' },
    { id: 'SPORTS', label: 'Deportes', emoji: '🏅' },
    { id: 'STANDUP', label: 'Stand-up', emoji: '🎤' },
    { id: 'THEATER', label: 'Teatro', emoji: '🎪' },
    { id: 'ART', label: 'Arte', emoji: '🖼️' },
    { id: 'FAMILY', label: 'Familiares', emoji: '👨🏻‍👩🏻‍👧🏻‍👦🏻' },
    { id: 'CONFERENCE', label: 'Conferencias', emoji: '💼' },
    { id: 'OTHER', label: 'Otros', emoji: '🫂' },
];

const allOption = RAW_CATEGORIES.find(c => c.id === 'ALL');
const otherOptions = RAW_CATEGORIES.filter(c => c.id !== 'ALL').sort((a, b) => a.label.localeCompare(b.label));

const CATEGORIES_CONFIG = [allOption!, ...otherOptions];

const VARIANT_MAP: Record<string, 'primary' | 'secondary' | 'tertiary' | 'warning'> = {
    'THEATER': 'primary',
    'CONCERT': 'secondary',
    'FESTIVAL': 'warning',
    'STANDUP': 'tertiary',
    'ART': 'primary',
    'SPORTS': 'secondary',
    'FAMILY': 'warning',
    'PARTY': 'tertiary',
    'CONFERENCE': 'tertiary',
    'OTHER': 'tertiary'
};

const HomeView: React.FC<HomeViewProps> = ({ openRegister }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isActiveUser = user?.role === 'user-organizer' || user?.role === 'user-client-buyer';

    const [events, setEvents] = useState<PublicEvent[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadEvents = async () => {
            setIsLoading(true);
            try {
                const response = await publicEventsService.getEvents(selectedCategory);
                setEvents(response.content);
            } catch (error) {
                console.error("Error cargando eventos:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadEvents();
    }, [selectedCategory]);

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        }).format(price);
    };

    // The event's wall time, shown as the organizer typed it
    const formatDate = (dateString: string) => {
        const day = formatEventDate(dateString, { day: 'numeric', month: 'short', year: 'numeric' });
        const time = formatEventDate(dateString, { hour: '2-digit', minute: '2-digit' });
        return `${day} • ${time}`;
    };

    return (
        <div className="animate-fade-in">

            {!isActiveUser && (
                <section className="st-gradient-hero py-16 sm:py-24 relative overflow-hidden">
                    <div className="st-container relative z-10 text-center">
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in-up">
                            Vive experiencias <span className="bg-gradient-to-r from-[var(--st-accent-primary)] to-[var(--st-accent-secondary)] bg-clip-text text-transparent">únicas</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-[var(--st-text-secondary)] mb-8 max-w-2xl mx-auto animate-fade-in-up animation-delay-100">
                            Descubre los mejores eventos de teatro, festivales, conferencias y más. Tu próxima aventura está a un clic de distancia.
                        </p>
                        <div className=" flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-200">
                            <Button variant="primary" className="st-btn st-btn-primary st-btn-lg" onClick={() => document.getElementById('categories-section')?.scrollIntoView({ behavior: 'smooth' })} icon={
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            }>
                                Explorar Eventos
                            </Button>
                            <Button variant="secondary" className="st-btn st-btn-secondary st-btn-lg" onClick={openRegister}>Crear Cuenta Gratis</Button>
                        </div>
                    </div>
                </section>
            )}

            <section id="categories-section" className="py-8 border-b border-[var(--st-border)] bg-white">
                <div className="st-container">

                    <div className="flex flex-wrap justify-center gap-3">
                        {CATEGORIES_CONFIG.map((cat) => (
                            <div
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className="flex-shrink-0 cursor-pointer transition-transform active:scale-95"
                            >
                                <Pill active={selectedCategory === cat.id}>
                                    {cat.emoji} {cat.label}
                                </Pill>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="events-section" className="py-12 sm:py-16">
                <div className="st-container">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="font-display text-2xl sm:text-3xl font-bold">
                            {selectedCategory === 'ALL' ? 'Eventos Destacados' : `Eventos de ${CATEGORIES_CONFIG.find(c => c.id === selectedCategory)?.label}`}
                        </h2>
                        {selectedCategory !== 'ALL' && (
                            <Button variant="ghost" size="sm" onClick={() => setSelectedCategory('ALL')}>Ver todos</Button>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-20">
                            <div className="st-spinner st-spinner-lg"></div>
                        </div>
                    ) : events.length === 0 ? (
                        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
                            <p className="text-xl text-[var(--st-text-muted)]">No hay eventos disponibles en esta categoría.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in">
                            {events.map((event) => {
                                const catConfig = CATEGORIES_CONFIG.find(c => c.id === event.category);
                                return (
                                    <div key={event.id} onClick={() => navigate(`/events/${event.id}`)} className="cursor-pointer">
                                        <EventCard
                                            emoji={catConfig?.emoji || '📅'}
                                            title={event.name}
                                            date={formatDate(event.startDate)}
                                            location={event.location}
                                            price={formatPrice(event.referencePriceFrom)}
                                            badgeText={catConfig?.label || "Disponible"}
                                            variant={VARIANT_MAP[event.category] || 'primary'}
                                            imageUrl={event.imageUrl}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {!isActiveUser && (
                <section className="py-16 sm:py-24">
                    <div className="st-container">
                        <div className="st-gradient-accent rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden">
                            <div className="absolute inset-0 bg-black/20"></div>
                            <div className="relative z-10">
                                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4 text-white">¿Organizas eventos?</h2>
                                <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">Únete a SuperTicket y llega a miles de personas.</p>
                                <Button className="st-btn st-btn-secondary st-btn-lg" size="lg" onClick={openRegister}>
                                    Comenzar como Organizador
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default HomeView;
